/**
 * Бейджи активных сделок партнёрского треда.
 *
 * CRM-специфичная мета, подключаемая к ядру Activity через дескриптор
 * `partnerActivityEntity` (`ThreadListMeta` / `ChatHeaderMeta`).
 */
import { useMemo } from 'react';
import { Center, Group, Text } from '@mantine/core';
import type { ActivityFeedItem } from '@ayocore/activity/server/repositories/ActivityThreadRepository';
import { activityUiHelpers } from '@ayocore/activity/client/extensions';
import { DealView } from '../components/Deals/views/DealView';

const LIST_PREVIEW_LIMIT = 3;
const HEADER_PREVIEW_LIMIT = 5;

type DealChipProps = {
  label: string;
  title: string;
  onClick?: () => void;
  background?: string;
};

function DealChip({ label, title, onClick, background }: DealChipProps) {
  const interactiveProps = onClick
    ? {
        role: 'button',
        tabIndex: 0,
        onClick,
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
          }
        },
      }
    : {};

  return (
    <Center
      {...interactiveProps}
      style={{
        minWidth: 0,
        maxWidth: 84,
        height: 18,
        padding: '0 6px',
        borderRadius: 999,
        background: background ?? 'var(--mantine-color-gray-1)',
        color: 'var(--mantine-color-gray-7)',
        fontSize: 10,
        fontWeight: 600,
        flexShrink: 0,
        ...(onClick ? { cursor: 'pointer' } : {}),
      }}
      title={title}
    >
      <Text
        size='10px'
        style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {label}
      </Text>
    </Center>
  );
}

function useActiveDealsPreview(item: ActivityFeedItem, limit: number) {
  const totalCount = Number(item.active_deals_total_count ?? 0);
  const previewIds = useMemo(() => {
    const ids = item.active_deals_preview_ids;
    return Array.isArray(ids)
      ? ids.filter(dealId => Number.isFinite(dealId) && dealId > 0).slice(0, limit)
      : [];
  }, [item.active_deals_preview_ids, limit]);
  const hiddenCount = Math.max(0, totalCount - previewIds.length);
  return { totalCount, previewIds, hiddenCount };
}

function openDeal(dealId: number): void {
  activityUiHelpers.openDrawer(<DealView dealId={dealId} isModal />);
}

/** Бейджи активных сделок в строке треда (список слева). */
export function ActiveDealsThreadListMeta({ item }: { item: ActivityFeedItem }) {
  const { totalCount, previewIds, hiddenCount } = useActiveDealsPreview(item, LIST_PREVIEW_LIMIT);
  if (totalCount <= 0 || previewIds.length === 0) return null;

  return (
    <Group gap={4} wrap='nowrap' style={{ minWidth: 0, overflow: 'hidden' }}>
      {previewIds.map(dealId => (
        <DealChip
          key={`${item.id}-deal-chip-${dealId}`}
          label={`#${dealId}`}
          title={`Сделка #${dealId}`}
        />
      ))}
      {hiddenCount > 0 ? (
        <DealChip
          label={`+${hiddenCount}`}
          title={`Еще ${hiddenCount} сделок`}
          background='var(--mantine-color-gray-2)'
        />
      ) : null}
    </Group>
  );
}

/** Кликабельные бейджи активных сделок в шапке чата. */
export function ActiveDealsChatHeaderMeta({ item }: { item: ActivityFeedItem }) {
  const { previewIds, hiddenCount } = useActiveDealsPreview(item, HEADER_PREVIEW_LIMIT);
  if (previewIds.length === 0) return null;

  return (
    <Group gap={4} wrap='nowrap' px='sm' py={4} style={{ minWidth: 0, overflow: 'hidden' }}>
      {previewIds.map(dealId => (
        <DealChip
          key={`chat-deal-chip-${dealId}`}
          label={`#${dealId}`}
          title={`Сделка #${dealId}`}
          onClick={() => openDeal(dealId)}
        />
      ))}
      {hiddenCount > 0 ? (
        <DealChip
          label={`+${hiddenCount}`}
          title={`Еще ${hiddenCount} сделок`}
          background='var(--mantine-color-gray-2)'
        />
      ) : null}
    </Group>
  );
}
